import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Badge,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  DoneAll as DoneAllIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

interface HeaderProps {
  onMenuClick: () => void;
  drawerWidth?: number;
}

const severityIcon = (s: string) => {
  if (s === 'critical') return <ErrorIcon fontSize="small" color="error" />;
  if (s === 'high') return <WarningIcon fontSize="small" color="warning" />;
  if (s === 'medium') return <InfoIcon fontSize="small" color="info" />;
  return <InfoIcon fontSize="small" color="disabled" />;
};

const severityColor = (s: string) => {
  if (s === 'critical') return 'error.50';
  if (s === 'high') return 'warning.50';
  return 'transparent';
};

const timeAgo = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `Hace ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days}d`;
};

export const Header: React.FC<HeaderProps> = ({ onMenuClick, drawerWidth = 0 }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await apiService.getNotificationSummary();
      const data = (res as any)?.data || res;
      setUnreadCount(data.unreadCount || 0);
      setRecentNotifications(data.recent || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchSummary();
    const interval = setInterval(fetchSummary, 120000);
    return () => clearInterval(interval);
  }, [fetchSummary]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
    fetchSummary();
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await apiService.markAllNotificationsRead();
      setUnreadCount(0);
      setRecentNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  const handleNotificationItemClick = async (notification: any) => {
    if (!notification.read) {
      try {
        await apiService.markNotificationRead(notification._id);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setRecentNotifications(prev => prev.map(n => n._id === notification._id ? { ...n, read: true } : n));
      } catch (e) { /* silent */ }
    }

    handleNotificationClose();

    // El técnico no tiene acceso a las pantallas de admin: sus avisos lo llevan a sus
    // propias vistas (finanzas y calendario).
    const esTecnico = user?.role === 'technician';

    if (notification.entityType === 'device' && notification.entityId && !esTecnico) {
      navigate(`/devices/${notification.entityId}`);
    } else if (notification.entityType === 'simcard' && notification.entityId && !esTecnico) {
      navigate(`/simcards/${notification.entityId}`);
    } else if (notification.entityType === 'technician' && notification.entityId) {
      navigate(esTecnico ? '/technician/finances' : `/technicians/${notification.entityId}/balance`);
    } else if (notification.entityType === 'appointment') {
      navigate(esTecnico ? '/technician/calendar' : '/calendar');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleProfileMenuClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleProfileMenuClose();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        color: 'text.primary',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
        ml: isMobile ? 0 : `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        <IconButton
          color="inherit"
          aria-label="toggle menu"
          onClick={onMenuClick}
          edge="start"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: 'text.primary',
            fontSize: { xs: '1rem', sm: '1.25rem' },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {isMobile ? 'Driports GPS' : 'Portal de Gestión GPS'}
        </Typography>

        {/* Notifications */}
        <Tooltip title="Notificaciones">
          <IconButton
            color="inherit"
            onClick={handleNotificationClick}
            sx={{ mr: { xs: 0.5, sm: 2 } }}
          >
            <Badge badgeContent={unreadCount > 99 ? '99+' : unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* User Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ mr: 2, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.role === 'admin' ? 'Administrador' : user?.role === 'technician' ? 'Técnico' : 'Asistente'}
            </Typography>
          </Box>

          <Tooltip title="Perfil de usuario">
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{ p: 0 }}
            >
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                {user?.name ? getInitials(user.name) : <AccountIcon />}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          PaperProps={{
            elevation: 8,
            sx: {
              mt: 1.5,
              minWidth: { xs: 200, sm: 220 },
              '& .MuiMenuItem-root': { px: 2, py: 1 },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleProfile}>
            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Mi Perfil</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleProfileMenuClose}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Configuración</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText><Typography color="error">Cerrar Sesión</Typography></ListItemText>
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          PaperProps={{
            elevation: 8,
            sx: {
              mt: 1.5,
              minWidth: { xs: 300, sm: 380 },
              maxWidth: { xs: '95vw', sm: 420 },
              maxHeight: 480,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Notificaciones
              </Typography>
              {unreadCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {unreadCount} sin leer
                </Typography>
              )}
            </Box>
            {unreadCount > 0 && (
              <Tooltip title="Marcar todas como leídas">
                <IconButton size="small" onClick={handleMarkAllRead} disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : <DoneAllIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Divider />

          {recentNotifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No hay notificaciones
              </Typography>
            </Box>
          ) : (
            recentNotifications.map((n: any) => (
              <MenuItem
                key={n._id}
                onClick={() => handleNotificationItemClick(n)}
                sx={{
                  py: 1.5,
                  px: 2,
                  alignItems: 'flex-start',
                  backgroundColor: n.read ? 'transparent' : severityColor(n.severity),
                  borderLeft: n.read ? 'none' : `3px solid`,
                  borderColor: n.severity === 'critical' ? 'error.main' : n.severity === 'high' ? 'warning.main' : 'info.main',
                  '&:hover': { backgroundColor: n.read ? 'action.hover' : severityColor(n.severity) },
                }}
              >
                <Box sx={{ mr: 1.5, mt: 0.5 }}>
                  {severityIcon(n.severity)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                    <Typography variant="body2" sx={{ fontWeight: n.read ? 400 : 600, lineHeight: 1.3 }} noWrap>
                      {n.title}
                    </Typography>
                    {!n.read && <DotIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }} noWrap>
                    {n.description}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                    {timeAgo(n.createdAt)}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          )}

          <Divider />
          <MenuItem
            onClick={() => {
              navigate('/admin/dashboard');
              handleNotificationClose();
            }}
            sx={{ justifyContent: 'center', py: 1.5 }}
          >
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              Ver todas en el Dashboard
            </Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
